import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import PlaceIcon from '@mui/icons-material/Place';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import RegionRow from 'components/RegionRow';
import FabDialog from 'components/FabDialog';
import AddRegionDialog from 'components/AddRegionDialog';

import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  CountryEditRegionsRefetchQuery,
} from '__generated__/CountryEditRegionsRefetchQuery.graphql';
import { CountryEditRegions_country$key } from '__generated__/CountryEditRegions_country.graphql';

type Props = {
  country: CountryEditRegions_country$key;
};

const CountryEditRegions: FC<Props> = props => {
  const { variables } = usePagination();

  const [country, regionsQuery] = useAvoidSuspense<
    CountryEditRegionsRefetchQuery,
    CountryEditRegions_country$key
  >(
    graphql`
      fragment CountryEditRegions_country on Country
      @refetchable(queryName: "CountryEditRegionsRefetchQuery") {
        regions(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id
              ...RegionRow_region
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }

        ...AddRegionDialog_country
      }
    `,
    props.country,
    Query
  );

  const connectionId = country.regions?.__id;

  const [deleteRegion, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = country.regions ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      regionsQuery.refetch({
        id: country.id,
        ...variables,
      });
    }, [country.id, regionsQuery, variables])
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Table
        pagination
        pageInfo={pageInfo ?? null}
        icon={<PlaceIcon />}
        loading={regionsQuery.loading || deleting}
        head={
          <TableRow>
            <TableCell>Pavadinimas</TableCell>
            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <RegionRow key={node.id} region={node} onDelete={deleteRegion} />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm" icon={<AddLocationIcon />}>
          <AddRegionDialog connectionId={connectionId} country={country} />
        </FabDialog>
      )}
    </Paper>
  );
};

export default CountryEditRegions;
